import { faClipboard } from "@fortawesome/free-regular-svg-icons";
import { faArrowDownWideShort, faArrowUpShortWide } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as moment from "moment";
import { FC, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ModalComponent from "src/core/components/modal/modal.component";
import HasPermission, { useHasPermissionForAnyAction } from "src/core/guards/has-permission";
import isModuleAndActionAvailable from "src/core/guards/is-module-and-action-available";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import capitalizeFirst from "src/shared/helpers/upper-case";
import NewPaginationComponent from "../pagination/new-pagination.component";
import "./new-table.scss";

interface TableHeader {
  label: String;
  title: String;
  width?: string;
  type?: "datetime" | string;
  redirectUrl?: string;
  isSorted?: boolean;
}

export interface EmptyConfig {
  title: string;
  description: string;
}

interface TableComponentProps {
  tableHeaders: TableHeader[] | any;
  allData?: any[];
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort?: any;
  actionConfig?: any;
  actionConfigName?: string;
  emptyConfig?: EmptyConfig;
  enableSelection?: boolean;
  isLoadingData?: boolean;
  count?: number;
  wrapperHeight?: string;
  moduleName?: any;
  actionName?: any;
  isFaultTable?: boolean;
  onRowClick?: (row: any) => void;
  selectedRowId?: any;
  rowIdKey?: string;
}

interface SelectedItem {
  page: any;
  value: any;
  status?: string;
}

const NewTableComponent: FC<TableComponentProps> = ({
  tableHeaders,
  allData = [],
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  actionConfig,
  actionConfigName = "Action",
  emptyConfig = null,
  enableSelection = false,
  isLoadingData = false,
  count = 11,
  wrapperHeight = "700px",
  moduleName,
  actionName,
  isFaultTable = false,
  onRowClick,
  selectedRowId,
  rowIdKey = "id",
}) => {
  const navigate = useNavigate();
  const [isDesc, setIsDesc] = useState(true);
  const [sortBy, setSortBy] = useState(null);
  const [disabledStatus, setDisabledStatus] = useState(false);
  const [areAllItemsSelectedOnThisPage, setAreAllItemsSelectedOnThisPage] =
    useState(false);
  const [isRenderModalOpen, isSetRenderModalOpen] = useState<boolean>(false);
  const [renderContent, setRrenderContent] = useState<any>(null);
  const [selectedData, setSelectedData] = useState<{
    headerTitle: string;
    data: any;
  }>({
    headerTitle: "",
    data: {},
  });

  const dispatch = useDispatch();

  const modules = useSelector((state: any) => state.navbar.modules);

  const selectedItems: SelectedItem[] = useSelector(
    (state: any) => state.table.selectedItems
  );

  const handleRenderCloseModal = () => {
    isSetRenderModalOpen(false);
  };
  const renderModalContent = (getContent: any) => {
    setRrenderContent(() => getContent);
    isSetRenderModalOpen(true);
  };

  const toggleSelect = (itemId: number, status: string) => {
    dispatch({
      type: "table/toggleSelect",
      payload: { page: current, value: itemId, status },
    });
  };

  const toggleSelectAll = (isFaultTable: boolean) => {
    let selectedItemsForPage: SelectedItem[] = [];
    let combinedSelectedItems;
    const allItemsSelected = areAllItemsSelectedOnThisPage;
    if (!allItemsSelected) {
      isFaultTable
        ? (selectedItemsForPage = data
          ?.filter((item: any) => item?.status !== "Resolved")
          ?.map((item: any) => ({
            page: current,
            value: item?.sensorId
              ? item?.sensorId
              : item?.id
                ? item?.id
                : item?.assetCode
                  ? item?.assetCode
                  : item?.deviceCode
                    ? item?.deviceCode
                    : item?.assetID
                      ? item?.assetID
                      : "",
            status: item?.status,
          })))
        : (selectedItemsForPage = data.map((item: any) => ({
          page: current,
          value: item?.sensorId
            ? item?.sensorId
            : item?.id
              ? item?.id
              : item?.assetCode
                ? item?.assetCode
                : item?.deviceCode
                  ? item?.deviceCode
                  : item?.assetID
                    ? item?.assetID
                    : "",
        })));

      combinedSelectedItems = [...selectedItems, ...selectedItemsForPage];
    } else {
      selectedItemsForPage = selectedItems.filter(
        (item: SelectedItem) => item.page !== current
      );

      combinedSelectedItems = [...selectedItemsForPage];
    }

    const uniqueValuesSet = combinedSelectedItems.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => t.value === item.value && t.page === item.page)
    );

    dispatch({
      type: "table/toggleSelectAll",
      payload: uniqueValuesSet,
    });
  };

  useEffect(() => {
    let totalItemSelectedForThisPage = 0;

    if (enableSelection) {
      let selectedItemsForThisPage: any = [];

      selectedItemsForThisPage = selectedItems?.filter(
        (item: SelectedItem) => item.page === current
      );

      if (isFaultTable) {
        const itemsResolvedInThisPage = data?.filter(
          (item: SelectedItem) => item?.status === "Resolved"
        );

        if (itemsResolvedInThisPage?.length == 10) {
          setAreAllItemsSelectedOnThisPage(false);
          return;
        }

        totalItemSelectedForThisPage =
          totalItemSelectedForThisPage + itemsResolvedInThisPage?.length;
      }

      let areAllItemsSelectedOnThisPage;

      totalItemSelectedForThisPage =
        totalItemSelectedForThisPage + selectedItemsForThisPage?.length;

      areAllItemsSelectedOnThisPage =
        data?.length > 0 && totalItemSelectedForThisPage === data?.length;

      const cleanedSelectedItems = selectedItems?.filter(
        (item) => !(item?.page === null && item?.value === null)
      );

      setAreAllItemsSelectedOnThisPage(
        cleanedSelectedItems?.length > 0 && areAllItemsSelectedOnThisPage
      );
    }
  }, [current, data, enableSelection, selectedItems]);

  const handleSort = (label: any) => {
    setSortBy(label);
    setIsDesc(!isDesc);
  };

  useEffect(() => {
    const sortOrder = isDesc ? "desc" : "asc";

    if (onChangeSort) onChangeSort(capitalizeFirst(sortBy), sortOrder);
  }, [isDesc]);

  const getDisabledStatus = async () => {
    if (moduleName && actionName) {
      const isAvailable = await isModuleAndActionAvailable(
        moduleName,
        actionName,
        modules
      );

      setDisabledStatus(!isAvailable);
    }
  };

  useEffect(() => {
    getDisabledStatus();
  }, []);

  useEffect(() => {
    return () => {
      dispatch({
        type: "table/toggleSelectAll",
        payload: [{ page: null, value: null, status: null }],
      });
    };
  }, []);

  const hasPermissionForAnyAction = useHasPermissionForAnyAction(actionConfig);

  // Row range shown in the "Showing X-Y of Z entries" footer text.
  const rangeStart = totalNumberOfRecords ? (current - 1) * pageSize + 1 : 0;
  const rangeEnd = totalNumberOfRecords
    ? Math.min(current * pageSize, totalNumberOfRecords)
    : 0;

  return (
    <>
      <SkeletonComponent
        wrapperHeight={wrapperHeight}
        skeletonHeight="50px"
        isLoading={isLoadingData}
        count={count}
        isShowCardStyle={true}
      >
        {data?.length ? (
          <>
            <div className="nt-table-wrapper">
              <table className="nt-table">
                <thead className="nt-table__head">
                  <tr>
                    {enableSelection && !disabledStatus && (
                      <th className="nt-table__th">
                        <input
                          type="checkbox"
                          style={{ cursor: "pointer" }}
                          checked={areAllItemsSelectedOnThisPage}
                          onChange={() => toggleSelectAll(isFaultTable)}
                        />
                      </th>
                    )}

                    {tableHeaders &&
                      tableHeaders?.length > 0 &&
                      tableHeaders.map((item: any, i: any) => (
                        <th
                          key={i}
                          className={`nt-table__th ${!item?.excludeSort ? "nt-table__th--sortable" : ""
                            }`}
                          style={{
                            width: item?.width ? item?.width : undefined,
                          }}
                          onClick={() =>
                            !item?.excludeSort && handleSort(item.label)
                          }
                        >
                          <div className="nt-table__th-content">
                            {item.title}
                            {!item?.excludeSort && (
                              <span className="nt-table__sort-icon nt-table__sort-icon--visible">
                                <FontAwesomeIcon
                                  icon={isDesc ? faArrowDownWideShort : faArrowUpShortWide}
                                />
                              </span>
                            )}
                          </div>
                        </th>
                      ))}

                    {actionConfig && hasPermissionForAnyAction && (
                      <th className="nt-table__th">{actionConfigName}</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data &&
                    data?.length > 0 &&
                    data.map((data: any, j: number) => (
                      <tr
                        key={j}
                        className={`nt-table__row ${
                          selectedRowId !== undefined &&
                          selectedRowId !== null &&
                          data[rowIdKey] === selectedRowId
                            ? "nt-table__row--active"
                            : ""
                        }`}
                        onClick={() => onRowClick && onRowClick(data)}
                        style={
                          onRowClick ? { cursor: "pointer" } : undefined
                        }
                      >
                        {enableSelection && !disabledStatus && (
                          <td className="nt-table__td">
                            <input
                              type="checkbox"
                              style={{ cursor: "pointer" }}
                              checked={selectedItems?.some(
                                (item) =>
                                  item.value ===
                                  (data.sensorId
                                    ? data.sensorId
                                    : data.id
                                      ? data.id
                                      : data.assetCode
                                        ? data.assetCode
                                        : data.deviceCode
                                          ? data.deviceCode
                                          : data.assetID
                                            ? data.assetID
                                            : "")
                              )}
                              onChange={() =>
                                toggleSelect(
                                  data?.sensorId
                                    ? data?.sensorId
                                    : data?.id
                                      ? data?.id
                                      : data?.assetCode
                                        ? data?.assetCode
                                        : data?.deviceCode
                                          ? data?.deviceCode
                                          : data?.assetID
                                            ? data?.assetID
                                            : "",
                                  data?.status
                                )
                              }
                              disabled={data?.status == "Resolved"}
                            />
                          </td>
                        )}

                        {tableHeaders.map((item: any, l: any) => (
                          <td
                            key={l}
                            className="nt-table__td"
                            colSpan={item?.colspan ? item?.colspan : null}
                          >
                            {item?.hasOwnProperty("type") &&
                              item?.type === "scrollable-html" ? (
                              <div className="scrollable-cell-wrapper">
                                <div
                                  className="scrollable-cell-content"
                                  style={{
                                    maxHeight: item?.maxHeight || '115px'
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: data[item?.label] || ''
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                onClick={(e) => {
                                  if (
                                    !e.currentTarget.querySelector(
                                      ".link-color.cursor-pointer"
                                    )
                                  ) {
                                    return;
                                  }

                                  if (
                                    item.hasOwnProperty("headerTitle") &&
                                    item.headerTitle &&
                                    item.hasOwnProperty("modalRenderProps") &&
                                    item.modalRenderProps
                                  ) {
                                    setSelectedData({
                                      headerTitle: item.headerTitle,
                                      data: data,
                                    });
                                    renderModalContent(item.modalRenderProps);
                                  }
                                }}
                                className="nt-table__cell-content"
                              >
                                {item.hasOwnProperty("type") &&
                                  item?.type === "datetime" ? (
                                  <>
                                    {data[item.label] ? (
                                      <>
                                        {" "}
                                        {moment
                                          .utc(data[item.label])

                                          .format("DD-MM-YYYY HH:mm:ss")}
                                      </>
                                    ) : (
                                      "-"
                                    )}
                                  </>
                                ) : item?.type === "date" ? (
                                  <>
                                    {data[item.label] ? (
                                      <>
                                        {" "}
                                        {moment(data[item.label]).format(
                                          "DD-MM-YYYY "
                                        )}{" "}
                                      </>
                                    ) : (
                                      "-"
                                    )}
                                  </>
                                ) : item?.type === "link" ? (
                                  <>
                                    <div
                                      style={{
                                        color: "#1976D2",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        if (
                                          item?.hasOwnProperty("isOpenInNewTable")
                                        ) {
                                          const newTab = window.open(
                                            `${item?.link}/${btoa(
                                              data[item.label]
                                            )}`,
                                            "_blank"
                                          );

                                          newTab?.focus();
                                        } else {
                                          navigate(
                                            item.hasOwnProperty(
                                              "linkableField"
                                            ) && item.linkableField
                                              ? `${item?.link}/${btoa(
                                                data[item.linkableField]
                                              )}`
                                              : `${item?.link}/${btoa(
                                                data[item.label]
                                              )}`
                                          );
                                        }
                                      }}
                                    >
                                      {data[item.label]}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {item.renderProps ? (
                                      <>{item.renderProps(data)}</>
                                    ) : (
                                      <>
                                        {typeof data[item.label] === "string" &&
                                          data[item.label]?.length
                                          ? capitalizeFirst(data[item.label])
                                          : typeof data[item.label] === "number"
                                            ? data[item.label]
                                            : typeof data[item.label] === "boolean"
                                              ? data[item.label]
                                                ? "True"
                                                : "False"
                                              : "-"}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        ))}

                        {actionConfig && !!actionConfig?.length && hasPermissionForAnyAction && (
                          <td className="nt-table__td">
                            <div className="flex justify-content-center">
                              {actionConfig.map((action: any, k: number) =>
                                action?.moduleName ? (
                                  <div key={k}>
                                    <HasPermission
                                      moduleName={action?.moduleName}
                                      actionName={action?.actionName}
                                      isRoute={false}
                                    >
                                      <div
                                        className={`table-actions  m-2 ${(action.hasOwnProperty("isDisplay") &&
                                          action.isDisplay(
                                            data,
                                            action.label
                                          )) ||
                                          !action.hasOwnProperty("isDisplay")
                                          ? ""
                                          : "d-none"
                                          } ${(action.hasOwnProperty("isDisabled") &&
                                            action.isDisabled(
                                              data,
                                              action.label
                                            )) ||
                                            !action.hasOwnProperty("isDisabled")
                                            ? ""
                                            : "disabled cursor-auto"
                                          }`}
                                        onClick={() =>
                                          (action.hasOwnProperty("isDisplay") &&
                                            action.isDisplay(
                                              data,
                                              action.label
                                            )) ||
                                            (action.hasOwnProperty("isDisabled") &&
                                              action.isDisabled(
                                                data,
                                                action.label
                                              )) ||
                                            (!action.hasOwnProperty("isDisplay") &&
                                              !action.hasOwnProperty("isDisabled"))
                                            ? action.event(
                                              data[action.actionProperty]
                                            )
                                            : null
                                        }
                                      >
                                        {action.label}
                                      </div>
                                    </HasPermission>
                                  </div>
                                ) : (
                                  <div
                                    key={k}
                                    className={`table-actions  m-2 ${(action.hasOwnProperty("isDisplay") &&
                                      action.isDisplay(data, action.label)) ||
                                      !action.hasOwnProperty("isDisplay")
                                      ? ""
                                      : "disabled"
                                      }`}
                                    onClick={() =>
                                      (action.hasOwnProperty("isDisplay") &&
                                        action.isDisplay(data, action.label)) ||
                                        !action.hasOwnProperty("isDisplay")
                                        ? action.event(
                                          data[action.actionProperty]
                                        )
                                        : null
                                    }
                                  >
                                    {action.label}
                                  </div>
                                )
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="nt-table__footer">
              <div className="nt-table__footer-info">
                {totalNumberOfRecords
                  ? `Showing ${rangeStart}-${rangeEnd} of ${totalNumberOfRecords} entries`
                  : null}
              </div>

              {onChangePage && totalNumberOfRecords && pageSize && current ? (
                <NewPaginationComponent
                  totalNumberOfRecords={totalNumberOfRecords}
                  pageSize={pageSize}
                  current={current}
                  onChangePage={onChangePage}
                />
              ) : null}
            </div>
          </>
        ) : (
          <div className="nt-table__empty">
            <div className="nt-table__empty-inner">
              <div className="nt-table__empty-icon">
                <FontAwesomeIcon icon={faClipboard} />
              </div>
              <h2 className="nt-table__empty-title">
                {emptyConfig ? emptyConfig?.title : "No Records Found"}
              </h2>
              <div>
                {emptyConfig ? emptyConfig?.description : "Add some new data please."}
              </div>
            </div>
          </div>
        )}
      </SkeletonComponent>

      {isRenderModalOpen && (
        <ModalComponent
          showModal={isRenderModalOpen}
          handleClose={handleRenderCloseModal}
          headerTitle={selectedData.headerTitle}
        >
          {renderContent ? (
            renderContent(selectedData.data)
          ) : (
            <div>No data</div>
          )}
        </ModalComponent>
      )}
    </>
  );
};

export default NewTableComponent;
